import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ArenaChallenge } from '../../../../api/group/arena.api';
import { formatVn, STATUS_LABEL } from '../../../arena/arena-format';
import {
  adminCancelChallenge,
  adminListChallenges,
  adminPublishChallenge,
} from '../../../../api/group/arena-admin.api';
import { hasAnyPermission, PERMISSION } from '../../../../utils/admin-permissions';
import { Plus } from 'lucide-react';
import { AdminButton, AdminCard, AdminPageHeader, AdminStatusPill } from '../../ui';
import ArenaChallengeForm from './ArenaChallengeForm';
import ArenaJudging from './ArenaJudging';

const NoAccess: React.FC = () => (
  <div className="p-6 text-gray-500 dark:text-gray-400">Bạn không có quyền truy cập khu vực này.</div>
);

type View = { kind: 'list' } | { kind: 'form'; challenge: ArenaChallenge | null } | { kind: 'judge'; challenge: ArenaChallenge };

const ManagementArenaInner: React.FC = () => {
  const [view, setView] = useState<View>({ kind: 'list' });
  const [list, setList] = useState<ArenaChallenge[]>([]);

  const load = useCallback(async () => {
    try {
      setList(await adminListChallenges());
    } catch (e: any) {
      toast.error(e?.message || 'Không tải được danh sách thử thách.');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const back = () => {
    setView({ kind: 'list' });
    load();
  };

  const publish = async (c: ArenaChallenge) => {
    try {
      await adminPublishChallenge(c.id);
      toast.success('Đã mở thử thách.');
      load();
    } catch (e: any) {
      toast.error(e?.message || 'Không mở được thử thách.');
    }
  };

  const cancel = async (c: ArenaChallenge) => {
    if (!window.confirm(`Huỷ thử thách "${c.title}"?`)) return;
    try {
      await adminCancelChallenge(c.id);
      toast.success('Đã huỷ thử thách.');
      load();
    } catch (e: any) {
      toast.error(e?.message || 'Không huỷ được thử thách.');
    }
  };

  if (view.kind === 'form') {
    return (
      <div className="adm-page">
        <ArenaChallengeForm challenge={view.challenge} onDone={back} onCancel={back} />
      </div>
    );
  }
  if (view.kind === 'judge') {
    return (
      <div className="adm-page">
        <ArenaJudging challenge={view.challenge} onBack={back} onFinalized={back} />
      </div>
    );
  }

  const tone = (st: string) =>
    st === 'PUBLISHED' ? 'success' : st === 'JUDGING' ? 'warning' : st === 'FINALIZED' ? 'brand' : st === 'CANCELLED' ? 'danger' : 'neutral';
  return (
    <div className="adm-page">
      <AdminPageHeader
        title="Thử thách Arena"
        description="Tạo, mở, chấm điểm và chốt kết quả các thử thách Arena."
        actions={
          <AdminButton elevated icon={<Plus size={18} />} onClick={() => setView({ kind: 'form', challenge: null })}>
            Tạo thử thách
          </AdminButton>
        }
      />
      <AdminCard flush>
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Tên</th>
                <th>Trạng thái</th>
                <th>Bắt đầu</th>
                <th>Hạn nộp bài</th>
                <th>Hạn chấm</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 600, color: 'var(--adm-heading)' }}>{c.title}</td>
                  <td>
                    <AdminStatusPill tone={tone(c.status)}>
                      {c.status === 'DRAFT' ? 'Nháp' : STATUS_LABEL[c.status] || c.status}
                    </AdminStatusPill>
                  </td>
                  <td>{formatVn(c.startAt)}</td>
                  <td>{formatVn(c.submitDeadline)}</td>
                  <td>{formatVn(c.judgingDeadline)}</td>
                  <td>
                    <div className="flex flex-wrap justify-end gap-2">
                      {(c.status === 'DRAFT' || c.status === 'PUBLISHED') && (
                        <AdminButton size={32} variant="outline" onClick={() => setView({ kind: 'form', challenge: c })}>
                          Sửa
                        </AdminButton>
                      )}
                      {c.status !== 'DRAFT' && c.status !== 'CANCELLED' && (
                        <AdminButton size={32} variant="fill" onClick={() => setView({ kind: 'judge', challenge: c })}>
                          Chấm
                        </AdminButton>
                      )}
                      {(c.status === 'DRAFT' || c.status === 'PUBLISHED' || c.status === 'JUDGING') && (
                        <AdminButton size={32} variant="danger-soft" onClick={() => cancel(c)}>
                          Huỷ
                        </AdminButton>
                      )}
                      {c.status === 'DRAFT' && (
                        <AdminButton size={32} variant="success" onClick={() => publish(c)}>
                          Mở
                        </AdminButton>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AdminCard>
    </div>
  );
};

const ManagementArena: React.FC = () => (hasAnyPermission(PERMISSION.ArenaManage) ? <ManagementArenaInner /> : <NoAccess />);

export default ManagementArena;
